import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, Subject } from 'rxjs';
import { WebSocketMessagingService } from './websocket-messaging.service';
import { LoggingService } from '../logging/logging.service';
import { ProgressUpdate } from "../../models/progress-update";
import { WebSocketConnectionService } from './websocket-connection.service';
import { takeUntil } from 'rxjs/operators';

/**
 * Service responsible for tracking and managing operation progress updates
 */
@Injectable({
  providedIn: 'root'
})
export class ProgressTrackingService implements OnDestroy {
  // Track active operations with progress updates
  private activeOperations = new Map<string, BehaviorSubject<ProgressUpdate>>();
  private operationsSubject = new BehaviorSubject<Map<string, BehaviorSubject<ProgressUpdate>>>(this.activeOperations);

  // Observable of all active operations
  public operations$ = this.operationsSubject.asObservable();

  // Track if we've already initialized
  private initialized = false;
  private progressSubscription: Subscription | null = null;

  // Subject for cleanup on destroy
  private destroy$ = new Subject<void>();

  constructor(
    private messagingService: WebSocketMessagingService,
    private connectionService: WebSocketConnectionService,
    private logger: LoggingService
  ) {
    // No need to track userEmail anymore - we use userEmail from JWT
  }

  /**
   * Implementation of OnDestroy interface
   */
  ngOnDestroy(): void {
    this.logger.debug('ProgressTrackingService', 'Service being destroyed');
    this.destroy$.next();
    this.destroy$.complete();
    this.dispose();
  }

  /**
   * Initialize progress tracking for the current session
   * Idempotent - can be called multiple times safely
   */
  public async initialize(): Promise<void> {
    // Prevent duplicate initialization
    if (this.initialized) {
      this.logger.debug('ProgressTrackingService', 'Already initialized, skipping');
      return;
    }

    const userEmail = this.getUserIdentifier();
    this.logger.info('ProgressTrackingService', `Initializing progress tracking service for user: ${userEmail}`);

    // Send subscription message for progress updates
    // Pass the user email to the server
    await this.messagingService.send('/app/subscribe-progress', userEmail);

    // Clean up any existing subscription to prevent duplicates
    if (this.progressSubscription) {
      this.progressSubscription.unsubscribe();
      this.progressSubscription = null;
    }

    // Subscribe to progress updates
    const progressObservable = await this.messagingService.subscribe<ProgressUpdate>(`/topic/progress/${userEmail}`);
    this.progressSubscription = progressObservable
      .subscribe(update => {
        this.handleProgressUpdate(update);
      });

    this.initialized = true;
    this.logger.info('ProgressTrackingService', `Subscribed to progress updates at /topic/progress/${userEmail}`);
  }

  /**
   * Get user identifier (email) from authenticated session.
   * This replaces the old userEmail logic which was unreliable (sessionStorage cleared on tab close).
   */
  public getUserIdentifier(): string {
    return this.connectionService.getUserIdentifier();
  }

  /**
   * Handle an incoming progress update
   */
  private handleProgressUpdate(update: ProgressUpdate): void {
    this.logger.info('ProgressTrackingService', 'Handling progress update', update);

    // Get or create a subject for this operation
    let operationSubject = this.activeOperations.get(update.operationId);

    if (!operationSubject) {
      // Create a new subject for this operation
      operationSubject = new BehaviorSubject<ProgressUpdate>(update);
      this.activeOperations.set(update.operationId, operationSubject);

      // Create a NEW MAP to ensure change detection works in Angular
      this.operationsSubject.next(new Map(this.activeOperations));

      this.logger.info('ProgressTrackingService', `Added new operation: ${update.operationId}, total active: ${this.activeOperations.size}`);
    } else {
      // Check if the update is actually different from the current value
      const currentValue = operationSubject.getValue();
      if (currentValue &&
        (currentValue.progress !== update.progress ||
          currentValue.message !== update.message)) {
        // Update the existing subject only if there's a change
        operationSubject.next(update);
        this.logger.info('ProgressTrackingService', `Updated operation: ${update.operationId}, progress: ${update.progress}%`);

        // Also emit the updated map to ensure subscribers are notified
        this.operationsSubject.next(new Map(this.activeOperations));
      }
    }

    // If progress is 100%, remove it after a delay
    if (update.progress === 100) {
      setTimeout(() => {
        this.activeOperations.delete(update.operationId);
        // Create a new Map to ensure change detection
        this.operationsSubject.next(new Map(this.activeOperations));
        this.logger.info('ProgressTrackingService', `Removed completed operation: ${update.operationId}, remaining: ${this.activeOperations.size}`);
      }, 3000); // Keep it visible for 3 seconds after completion
    }
  }

  /**
   * Manually register a progress update (useful for testing or local operations)
   */
  public registerProgressUpdate(update: ProgressUpdate): void {
    this.handleProgressUpdate(update);
  }

  /**
   * Get the latest progress update for a specific operation type
   *
   * @param type The operation type to look for
   * @returns Observable of the most recent progress update for that type
   */
  public getProgressForType(type: string): Observable<ProgressUpdate | null> {
    // Create a subject to track the latest update of this type
    const subject = new BehaviorSubject<ProgressUpdate | null>(null);

    // Subscribe to all operations
    this.operations$.subscribe(operations => {
      // Find the most recent operation of this type
      let latestUpdate: ProgressUpdate | null = null;
      let maxProgress = 0;

      operations.forEach(opSubject => {
        const update = opSubject.getValue();
        if (update && update.type === type) {
          // Prioritize updates with higher progress values
          if (!latestUpdate || update.progress > maxProgress) {
            latestUpdate = update;
            maxProgress = update.progress;
          }
        }
      });

      // Only emit if we have a new update or need to clear (null)
      const currentValue = subject.getValue();

      // Check if values are different
      const hasChanged = latestUpdate !== currentValue;

      // Type guard to ensure safe access to progress property
      const progressChanged =
        latestUpdate !== null && currentValue !== null &&
        (latestUpdate as ProgressUpdate).progress !== (currentValue as ProgressUpdate).progress;

      if (hasChanged || progressChanged) {
        const progressValue = latestUpdate ? (latestUpdate as ProgressUpdate).progress : null;
        this.logger.info('ProgressTrackingService', `Emitting updated progress for ${type}: ${progressValue}%`);
        subject.next(latestUpdate);
      }
    });

    return subject.asObservable();
  }

  /**
   * Clean up resources when service is destroyed
   */
  public dispose(): void {
    if (this.progressSubscription) {
      this.progressSubscription.unsubscribe();
      this.progressSubscription = null;
    }

    this.initialized = false;
    this.logger.debug('ProgressTrackingService', 'Resources disposed');
  }
}